/**
 * NMEA 0183 decoding for GPS receivers that stream text over a serial (COM) port — USB GPS pucks and
 * laptop built-in GPS modules. We use RMC (position, speed, course, fix valid) and GGA (fix quality,
 * satellites, HDOP). Any talker (GP, GN, GL, GA, BD).
 */

export interface NmeaFix {
  lat: number;
  lng: number;
  /** Rough 1-sigma accuracy in meters (HDOP × 5 m), or null if unknown. */
  accuracyM: number | null;
  speedMps: number | null;
  /** Course over ground, degrees true. */
  heading: number | null;
  satellites: number | null;
  /** UTC time of the fix from the receiver, if present (ISO). */
  time: string | null;
}

/** True when the sentence's *XX checksum matches (sentences without one are accepted). */
export function nmeaChecksumOk(line: string): boolean {
  const s = line.trim();
  if (!s.startsWith('$')) return false;
  const star = s.lastIndexOf('*');
  if (star < 0) return true;
  let x = 0;
  for (let i = 1; i < star; i++) x ^= s.charCodeAt(i);
  return s.slice(star + 1, star + 3).toUpperCase() === x.toString(16).toUpperCase().padStart(2, '0');
}

function coord(v: string | undefined, hemi: string | undefined, degDigits: number): number | null {
  if (!v || !hemi) return null;
  const deg = Number(v.slice(0, degDigits));
  const min = Number(v.slice(degDigits));
  if (!Number.isFinite(deg) || !Number.isFinite(min)) return null;
  const d = deg + min / 60;
  return hemi === 'S' || hemi === 'W' ? -d : d;
}

/**
 * Stateful decoder: feed it raw text chunks as they arrive; it returns a fix each time an RMC with
 * a valid position comes in (merged with the latest GGA for accuracy and satellites).
 */
export class NmeaDecoder {
  private buf = '';
  private gga: { sats: number | null; hdop: number | null; quality: number } | null = null;
  /** Count of good sentences seen — used to detect the right baud rate. */
  goodSentences = 0;

  push(chunk: string): NmeaFix[] {
    this.buf += chunk;
    if (this.buf.length > 8192) this.buf = this.buf.slice(-2048);
    const out: NmeaFix[] = [];
    let nl: number;
    while ((nl = this.buf.search(/\r?\n/)) >= 0) {
      const line = this.buf.slice(0, nl).trim();
      this.buf = this.buf.slice(nl + 1).replace(/^\n/, '');
      const f = this.line(line);
      if (f) out.push(f);
    }
    return out;
  }

  line(raw: string): NmeaFix | null {
    const start = raw.indexOf('$');
    if (start < 0) return null;
    const s = raw.slice(start);
    if (!nmeaChecksumOk(s)) return null;
    const body = s.split('*')[0]!;
    const f = body.split(',');
    const type = f[0]!.slice(3);
    if (!/^\$[A-Z]{2}[A-Z]{3}$/.test(f[0]!)) return null;
    this.goodSentences++;
    if (type === 'GGA') {
      this.gga = { quality: Number(f[6] || 0), sats: f[7] ? Number(f[7]) : null, hdop: f[8] ? Number(f[8]) : null };
      return null;
    }
    if (type !== 'RMC' || f[2] !== 'A') return null; // A = valid fix, V = no fix
    const lat = coord(f[3], f[4], 2);
    const lng = coord(f[5], f[6], 3);
    if (lat == null || lng == null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    const knots = f[7] ? Number(f[7]) : null;
    const course = f[8] ? Number(f[8]) : null;
    const t = f[1] ?? '';
    const d = f[9] ?? '';
    const time =
      t.length >= 6 && d.length === 6
        ? `${Number(d.slice(4, 6)) >= 80 ? 19 : 20}${d.slice(4, 6)}-${d.slice(2, 4)}-${d.slice(0, 2)}T${t.slice(0, 2)}:${t.slice(2, 4)}:${t.slice(4, 6)}Z`
        : null;
    const g = this.gga && this.gga.quality > 0 ? this.gga : null;
    return {
      lat,
      lng,
      accuracyM: g?.hdop != null && Number.isFinite(g.hdop) ? Math.max(3, Math.round(g.hdop * 5)) : null,
      speedMps: knots != null && Number.isFinite(knots) ? knots * 0.514444 : null,
      heading: course != null && Number.isFinite(course) ? course : null,
      satellites: g?.sats ?? null,
      time,
    };
  }
}

/** Build a checksummed sentence (used by tests and the browser test's fake GPS). */
export function nmeaSentence(body: string): string {
  let x = 0;
  for (let i = 0; i < body.length; i++) x ^= body.charCodeAt(i);
  return `$${body}*${x.toString(16).toUpperCase().padStart(2, '0')}`;
}

/** RMC + GGA pair for a position (for tests / simulators). */
export function nmeaFor(lat: number, lng: number, opts: { knots?: number; course?: number; hdop?: number; sats?: number; at?: Date } = {}): string {
  const at = opts.at ?? new Date();
  const dm = (v: number, degDigits: number) => {
    const a = Math.abs(v);
    const deg = Math.floor(a);
    const min = (a - deg) * 60;
    return `${String(deg).padStart(degDigits, '0')}${min.toFixed(4).padStart(7, '0')}`;
  };
  const hh = at.toISOString();
  const t = `${hh.slice(11, 13)}${hh.slice(14, 16)}${hh.slice(17, 19)}.00`;
  const d = `${hh.slice(8, 10)}${hh.slice(5, 7)}${hh.slice(2, 4)}`;
  const la = `${dm(lat, 2)},${lat < 0 ? 'S' : 'N'}`;
  const lo = `${dm(lng, 3)},${lng < 0 ? 'W' : 'E'}`;
  return [
    nmeaSentence(`GPGGA,${t},${la},${lo},1,${opts.sats ?? 9},${(opts.hdop ?? 0.9).toFixed(1)},180.0,M,-32.0,M,,`),
    nmeaSentence(`GPRMC,${t},A,${la},${lo},${(opts.knots ?? 0).toFixed(1)},${(opts.course ?? 0).toFixed(1)},${d},,,A`),
  ].join('\r\n') + '\r\n';
}
